import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { createColumnHelper } from "@tanstack/react-table";
import { z } from "zod";

import dates from "core/dates";
import useFilters from "core/hooks/useFilters";

import Box from "ui/atom/Box";
import Image from "ui/atom/Image";
import Token from "ui/atom/Token";
import Button from "ui/base/Button";

import type { ContentInfoApi } from "api/models/content";

import type { FormData } from "features/form/models/types";
import { useForm } from "features/form/fabric";
import { useMutateContent, useMutateFeatured, useQueryContent, useQueryContents } from "./data";


export const useContents = () => {
	const { filters, setFilters } = useFilters("/content/");
	const query = useQueryContents(filters.type, filters.search, filters.page);
	const columnHelper = createColumnHelper<ContentInfoApi>();
	const mutation = useMutateFeatured();
	const theme = useTheme();

	const columns = [
		columnHelper.display({
			header: "Title",
			cell: ({ row }) => (
				<Box gap={0.5} sx={{ alignItems: "center" }}>
					<Image.Cover src={row.original.thumbUrl} size={50} />
					<Typography>{row.original.title}</Typography>
				</Box>
			),
			size: 150
		}),
		columnHelper.display({
			header: "Partner",
			cell: ({ row }) => <Image src={row.original.partner?.logo.dark} sx={{ width: 100 }} />,
			size: 120
		}),
		columnHelper.accessor("publishedOn", {
			header: "Published on",
			cell: info => dates.brief(info.getValue()),
			size: 120

		}),
		columnHelper.accessor("tokens", {
			header: "Cost",
			cell: info => <Token value={info.getValue()} />,
			size: 20
		}),
		columnHelper.display({
			header: "Actions",
			cell: ({ row }) => (
				<Box gap={0.5}>
					<Button.Icon icon="star" fill={row.original.featured ? theme.palette.coin.main : ""} onClick={() => mutation.mutate({ id: row.original.id, value: !row.original.featured })} />
					<Button.Icon to="/content/$id" params={{ id: row.original.id.toString() }} icon="chevron-right" />
				</Box>
			),
			size: 100
		})
	];

	return { columns, query, filters, setFilters };
};

const ContentSchema = z.object({
	title: z.string().nonempty(),
	description: z.string(),
	publishedOn: z.iso.datetime(),
	url: z.string(),
	tokens: z.number(),
	featured: z.boolean()
});

export const useContent = () => {
	const query = useQueryContent();
	const mutation = useMutateContent();
	const featured = useMutateFeatured();

	const data = query.data;

	const defaultValues: FormData<typeof ContentSchema> = {
		title: data?.title || "",
		description: data?.description || "",
		publishedOn: data?.publishedOn || "",
		url: data?.url || "",
		tokens: data?.tokens || 1,
		featured: data?.featured || false
	};

	const form = useForm({
		defaultValues,
		validators: {
			onSubmit: ContentSchema
		},
		canSubmitWhenInvalid: true,
		onSubmit: async ({ value }) => {
			await mutation.mutateAsync({
				id: query.data.id,
				body: {
					title: value.title,
					description: value.description,
					url: value.url,
					tokens: value.tokens,
					publishedOn: value.publishedOn
				}
			});
		}

	});
	return { featured, form, query };
};